export const ASSET_CACHE_KEY = 'vb_asset_v';
const VERSION_RELOAD_FLAG = 'vb_asset_reload';
const CHUNK_RELOAD_FLAG = 'vb_chunk_reload';
const IDLE_RELOAD_FLAG = 'vb_idle_reload';
const RELOADED_FOR_VERSION_KEY = 'vb_reloaded_for_v';

export const DEPLOY_ASSET_VERSION = import.meta.env.VITE_ASSET_CACHE || '20260831egtvip1';

export const CDN_BASE = String(import.meta.env.VITE_CDN_URL || 'https://vurgun-cas-cdn.b-cdn.net').replace(/\/$/, '');
export const CDN_FALLBACK_BASE = String(import.meta.env.VITE_CDN_FALLBACK_URL || 'https://vurgunbet200.com').replace(/\/$/, '');

/** Game icons live on origin; Bunny storage zone is incomplete for new GH mirrors. */
function uploadsCdnBase(pathname) {
  // Prefer origin for all /uploads — Bunny edges often cache 404 for restored CMS files.
  if (pathname.startsWith('/uploads/')) {
    return CDN_FALLBACK_BASE || CDN_BASE;
  }
  return CDN_BASE;
}

const SITE_UPLOAD_HOSTS = new Set([
  'vurgunbet.com',
  'www.vurgunbet.com',
  'bo.vurgunbet.com',
  'vurgunbet200.com',
  'www.vurgunbet200.com',
  'vurgunbet201.com',
  'www.vurgunbet201.com',
  'vurgunbet202.com',
  'www.vurgunbet202.com',
]);


let reloadScheduled = false;

export function cdnUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^(data:|blob:)/i.test(trimmed)) return trimmed;
  if (!CDN_BASE && !CDN_FALLBACK_BASE) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.pathname.startsWith('/uploads/')) {
        const host = parsed.hostname.toLowerCase();
        let baseHost = '';
        let fallbackHost = '';
        try { baseHost = new URL(CDN_BASE).hostname; } catch {}
        try { fallbackHost = new URL(CDN_FALLBACK_BASE).hostname; } catch {}
        // Keep already-correct cas CDN / fallback hosts as-is.
        if (baseHost && host === baseHost) {
          return `${CDN_BASE}${parsed.pathname}${parsed.search}`;
        }
        if (fallbackHost && host === fallbackHost) {
          return `${CDN_FALLBACK_BASE}${parsed.pathname}${parsed.search}`;
        }
        if (
          host.endsWith('b-cdn.net') ||
          SITE_UPLOAD_HOSTS.has(host) ||
          host === 'localhost' ||
          host === '127.0.0.1'
        ) {
          const base = uploadsCdnBase(parsed.pathname);
          return `${base}${parsed.pathname}${parsed.search}`;
        }
      }
    } catch {
    }
    return trimmed;
  }
  if (trimmed.startsWith('/uploads/')) {
    const base = uploadsCdnBase(trimmed);
    return `${base}${trimmed}`;
  }
  return trimmed;
}

export function cdnFallbackUrl(url) {
  if (!url || typeof url !== 'string' || !CDN_FALLBACK_BASE) return url;
  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      if (!parsed.pathname.startsWith('/uploads/')) return url;
      return `${CDN_FALLBACK_BASE}${parsed.pathname}${parsed.search}`;
    }
  } catch {}
  const path = url.startsWith('/uploads/') ? url : null;
  if (path) return `${CDN_FALLBACK_BASE}${path}`;
  const rewritten = cdnUrl(url);
  try {
    const parsed = new URL(rewritten);
    if (parsed.pathname.startsWith('/uploads/')) {
      return `${CDN_FALLBACK_BASE}${parsed.pathname}${parsed.search}`;
    }
  } catch {}
  return rewritten;
}


const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError|error loading dynamically imported module/i;

export function isChunkLoadError(err) {
  const message = String(err?.message || err || '');
  const name = String(err?.name || '');
  return CHUNK_ERROR_RE.test(message) || name === 'ChunkLoadError';
}

export function readAssetCacheVersion() {
  try {
    return localStorage.getItem(ASSET_CACHE_KEY) || '';
  } catch {
    return '';
  }
}

export function bumpAssetCacheVersion() {
  const version = String(Date.now());
  try {
    localStorage.setItem(ASSET_CACHE_KEY, version);
  } catch {}
  return version;
}

export function clearReloadFlags() {
  try {
    sessionStorage.removeItem(VERSION_RELOAD_FLAG);
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    sessionStorage.removeItem(IDLE_RELOAD_FLAG);
  } catch {}
}

export function resetReloadSchedule() {
  reloadScheduled = false;
}

function anyReloadFlagSet() {
  try {
    return (
      sessionStorage.getItem(VERSION_RELOAD_FLAG) === '1' ||
      sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1' ||
      sessionStorage.getItem(IDLE_RELOAD_FLAG) === '1'
    );
  } catch {
    return false;
  }
}

export function hardReloadApp({ reason = 'asset' } = {}) {
  if (typeof window === 'undefined') return false;
  if (reloadScheduled) return false;

  const flag =
    reason === 'chunk' ? CHUNK_RELOAD_FLAG
      : reason === 'idle' ? IDLE_RELOAD_FLAG
        : VERSION_RELOAD_FLAG;

  try {
    if (reason === 'version' || reason === 'asset') {
      if (sessionStorage.getItem(RELOADED_FOR_VERSION_KEY) === DEPLOY_ASSET_VERSION) {
        return false;
      }
    }
    if (sessionStorage.getItem(flag) === '1') return false;
    if (anyReloadFlagSet()) return false;
    sessionStorage.setItem(flag, '1');
    if (reason === 'version' || reason === 'asset') {
      sessionStorage.setItem(RELOADED_FOR_VERSION_KEY, DEPLOY_ASSET_VERSION);
    }
  } catch {
  }

  reloadScheduled = true;

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_vb')) {
      url.searchParams.delete('_vb');
      window.location.replace(url.pathname + url.search + url.hash);
    } else {
      window.location.reload();
    }
  } catch {
    window.location.reload();
  }
  return true;
}

export async function reloadIfDeployStale() {
  if (typeof window === 'undefined') return false;
  try {
    const path = window.location.pathname || '';
    if (path.includes('/casino/game/')) return false;
    const res = await fetch(`/asset-version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return false;
    const data = await res.json();
    const remote = String(data?.v || '').trim();
    if (!remote || remote === DEPLOY_ASSET_VERSION) return false;
    try {
      localStorage.setItem(ASSET_CACHE_KEY, remote);
      sessionStorage.removeItem(VERSION_RELOAD_FLAG);
      sessionStorage.removeItem(RELOADED_FOR_VERSION_KEY);
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      sessionStorage.removeItem(IDLE_RELOAD_FLAG);
    } catch {}
    reloadScheduled = false;
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

export function ensureAssetCacheVersion() {
  const current = readAssetCacheVersion();
  if (current === DEPLOY_ASSET_VERSION) {
    if (!reloadScheduled) clearReloadFlags();
    return current;
  }

  try {
    localStorage.setItem(ASSET_CACHE_KEY, DEPLOY_ASSET_VERSION);
  } catch {}

  if (current && typeof window !== 'undefined') {
    hardReloadApp({ reason: 'version' });
    return DEPLOY_ASSET_VERSION;
  }

  return DEPLOY_ASSET_VERSION;
}

function shouldBustAssetCache(pathOnly) {
  return (
    pathOnly.startsWith('/uploads/') ||
    pathOnly.startsWith('/content/') ||
    pathOnly.startsWith('/static/content/') ||
    pathOnly.includes('/Casino/categories/')
  );
}

export function withAssetCacheVersion(url) {
  if (!url || typeof url !== 'string') return url;
  const rewritten = cdnUrl(url);
  const pathOnly = rewritten.replace(/^https?:\/\/[^/]+/i, '');
  if (!shouldBustAssetCache(pathOnly)) return rewritten;
  const version = readAssetCacheVersion() || DEPLOY_ASSET_VERSION;
  if (!version) return rewritten;

  const qIndex = rewritten.indexOf('?');
  if (qIndex !== -1) {
    const base = rewritten.slice(0, qIndex);
    const params = new URLSearchParams(rewritten.slice(qIndex + 1));
    const existing = params.get('v');
    // Keep SoftSwiss/pm.by remirror tags — do not replace with deploy version.
    if (existing && /^(sspm1|sspm2|softswiss2|pmby\d*)/i.test(existing)) return rewritten;
    if (existing && existing === version) return rewritten;
    params.set('v', version);
    return `${base}?${params.toString()}`;
  }

  return `${rewritten}?v=${encodeURIComponent(version)}`;
}

export function lazyRetry(factory, { retries = 2 } = {}) {
  return () =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;

      if (retries < 1) {
        hardReloadApp({ reason: 'chunk' });
        throw err;
      }

      return new Promise((resolve, reject) => {
        window.setTimeout(() => {
          lazyRetry(factory, { retries: retries - 1 })().then(resolve, reject);
        }, 350 + (2 - retries) * 250);
      });
    });
}
